
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { BrainCircuit, User, LogOut } from "lucide-react";

export function LandingNavbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleLogout = () => {
    logout();
    navigate({ to: "/login" });
  };

  const userInitial = user?.email?.charAt(0).toUpperCase() ?? "U";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-transparent backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 font-semibold tracking-tight text-white"
        >
          <BrainCircuit className="size-5" />
          <span className="text-sm sm:text-base">CBEET</span>
          <span className="ml-1 hidden rounded-full border border-white/30 px-2 py-0.5 text-[10px] font-medium text-white/90 sm:inline">
            CBT
          </span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                <Avatar size="sm">
                  <AvatarFallback>{userInitial}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8}>
                <DropdownMenuGroup>
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {user?.email}
                      </span>
                      {user?.role === "admin" && (
                        <span className="text-xs text-muted-foreground">
                          {t("nav.admin_role")}
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <Link to="/dashboard">
                  <DropdownMenuItem className="cursor-pointer">
                    <User className="mr-2 size-4" />
                    {t("nav.select_exam")}
                  </DropdownMenuItem>
                </Link>
                <Link to="/profile">
                  <DropdownMenuItem className="cursor-pointer">
                    <User className="mr-2 size-4" />
                    {t("nav.profile")}
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 size-4" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/register">
              <Button
                size="sm"
                className="cursor-pointer bg-white text-blue-700 hover:bg-blue-50"
              >
                {t("nav.register")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
